import type { SiteRagReport } from '../../types/siteRag';

export type ReportPresentationMode =
  | 'continuous-html'
  | 'continuous-reader-unavailable';

type ReportPresentationIdentity = Pick<SiteRagReport, 'reportRevision'> & {
  reportRevisionId?: unknown;
  artifactState?: unknown;
  deliveryDecision?: unknown;
  _metadataOnly?: unknown;
  _portfolioSlimBody?: unknown;
};

/**
 * Firestore list metadata can identify a V2 revision before the complete
 * revision pointer is hydrated. Treat those records as V2 so a missing body
 * can never fall through to the polished legacy DOM renderer.
 */
export function hasImmutableReportIdentity(report: ReportPresentationIdentity): boolean {
  if (Object.prototype.hasOwnProperty.call(report, 'reportRevision')) return true;
  if (typeof report.reportRevisionId === 'string' && report.reportRevisionId.trim()) return true;
  if (
    report.artifactState === 'ready'
    || report.artifactState === 'staged'
    || report.artifactState === 'failed'
  ) return true;
  return Boolean(
    report.deliveryDecision
    && typeof report.deliveryDecision === 'object'
    && (report.deliveryDecision as { schemaVersion?: unknown }).schemaVersion === 'delivery-decision-v1',
  );
}

/**
 * Select the presentation surface that may render a report revision.
 *
 * Reading and exporting are deliberately separate. A hydrated semantic report
 * always uses the responsive, continuous HTML reader; immutable PDF pagination
 * is an on-demand artifact concern. Metadata-only list records still fail
 * closed until their complete report body has been hydrated.
 *
 * `fixedViewerEnabled` remains in the signature for rollout-call compatibility.
 * It no longer controls the normal reading surface.
 */
export function resolveReportPresentationMode(
  report: ReportPresentationIdentity,
  _fixedViewerEnabled: boolean,
): ReportPresentationMode {
  return report._metadataOnly === true || report._portfolioSlimBody === true
    ? 'continuous-reader-unavailable'
    : 'continuous-html';
}
